/**
 * Activity Service - Frontend
 * Unified activity management for both standalone activities and trip activities
 * Communicates with backend /api/activity-instances endpoints
 */

import { API_BASE } from '../config/api';
import AuthService from './authService';
import { isGooglePhotoUrl } from '../utils/googlePhotoProxy';
import { tipsForSave } from '../utils/tipPayload';
import { getLocalDateString } from '../utils/dateUtils';

class ActivityService {
  // Use /api/activity-instances endpoints (backend route)
  static API_BASE = `${API_BASE}/api/activity-instances`;

  /**
   * Normalize activity data from server (snake_case) to frontend format (camelCase)
   * This ensures consistent data shape across all components
   */
  static normalizeActivity(activity) {
    if (!activity) return null;
    return {
      ...activity,
      // Map primary key
      id: activity.activity_id,
      // Map title from place_name or custom_title
      title: activity.place_name || activity.custom_title || activity.title || 'Untitled Activity',
      // Map image - the user's chosen custom_image, then cover_photo (the
      // cover-photo cascade, Google last; only on fetches that pass
      // with_cover=1), then the event image. There is no place_photos fallback: it built
      // /api/photos/<name>, a path nothing serves, and a Google photo picked
      // here would skip the cascade's free sources and its credit lookup.
      image: activity.custom_image || activity.cover_photo || activity.event_image || null,
      // Map dates to camelCase
      completedAt: activity.completed_at,
      scheduledDate: activity.scheduled_date,
      scheduledTime: activity.scheduled_time,
      createdAt: activity.created_at,
      updatedAt: activity.updated_at,
      // Map place data
      placeId: activity.place_id,
      placeName: activity.place_name,
      placeAddress: activity.place_address,
      placePhotos: activity.place_photos,
      // Map event data (from shared_events JOIN)
      eventId: activity.event_id,
      customImage: activity.custom_image,
      ticketUrl: activity.ticket_url,
      eventTitle: activity.event_title,
      eventDescription: activity.event_description,
      eventVenue: activity.event_venue,
      eventCity: activity.event_city,
      eventState: activity.event_state,
      eventImage: activity.event_image,
      eventCategory: activity.event_category,
      eventSegment: activity.event_segment,
      eventGenre: activity.event_genre,
      eventSubGenre: activity.event_sub_genre,
      eventPrice: activity.event_price,
      // Map trip data
      tripId: activity.trip_id,
      tripName: activity.trip_name,
      tripDayId: activity.trip_day_id,
      // Map other fields
      contentType: activity.content_type,
      customTitle: activity.custom_title,
      customDescription: activity.custom_description,
      customLocation: activity.custom_location,
      customCoordinates: activity.custom_coordinates,
    };
  }

  // Use centralized AuthService.handleResponse for auth error handling

  // =====================================================
  // SCHEDULE ACTIVITIES
  // =====================================================

  /**
   * Schedule a standalone activity (not part of a trip)
   * @param {object} activityData - Activity details
   * @returns {object} Result with activity instance
   */
  static async scheduleActivity(activityData) {
    try {
      const {
        place_id,           // Google place_id (for places)
        event_id,           // Ticketmaster event_id (for events)
        title,              // Activity title
        description,        // Activity description
        location,           // Location string
        typedLocation,      // A location the user typed (place rows; C355)
        coordinates,        // {lat, lng}
        scheduledDate,      // YYYY-MM-DD
        scheduledTime,      // HH:MM
        duration,           // minutes
        notes,
        image               // Image URL (for events)
      } = activityData;

      // Use place_id if available, otherwise check for event_id
      const placeId = place_id || null;
      const eventId = event_id || null;

      // Determine content type
      let contentType = 'custom';
      let contentId = null;
      if (placeId) {
        contentType = 'place';
        contentId = placeId;
      } else if (eventId) {
        contentType = 'ticketmaster_event';
        contentId = eventId;
      }

      // A place sends no Google text (C355, as the review form since C341;
      // C274): a search result's or card's title, address and description
      // are Google's for a place we don't name. Only a location the user
      // typed goes, as typed_location; the server names the place from our
      // own data, else the readers name it at read time. A custom activity's
      // words are all typed (the calendar's form) and an event's are ours,
      // so those still go as sent. An event is scheduled as an event (C374(1)):
      // one carrying its venue's place id goes with both ids — the venue rides
      // along as the row's place link — and with its own (our) text; the
      // server keeps that text only for an event id it knows. A typed
      // location goes whenever a place id does (C381(1)): with an event id
      // the server doesn't know beside it, the row is filed as the place and
      // keeps only typed_location.
      const isPlace = contentType === 'place' && !eventId;
      const response = await fetch(ActivityService.API_BASE, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({
          trip_id: null,     // Standalone activity
          content_id: contentId,
          content_type: contentType,
          event_id: eventId,
          custom_title: isPlace ? null : title,
          custom_description: isPlace ? null : description,
          custom_location: isPlace ? null : location,
          typed_location: placeId ? (typedLocation || null) : null,
          custom_coordinates: coordinates,
          // A Google photo is never stored outside google_place_cache; the
          // server drops it too, and the card falls back to the place's photo.
          custom_image: isGooglePhotoUrl(image) ? null : image,
          scheduled_date: scheduledDate,
          scheduled_time: scheduledTime,
          duration,
          notes
        })
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error scheduling activity:', error);
      return { success: false, error: error.message };
    }
  }

  // =====================================================
  // GET ACTIVITIES
  // =====================================================

  /**
   * Get user's activities with filters
   * @param {object} filters - Optional filters
   * @returns {array} Array of activities
   */
  static async getActivities(filters = {}) {
    try {
      const {
        status,           // 'planned', 'in_progress', 'completed', 'skipped', 'cancelled', 'archived'
        trip_id,          // Specific trip
        start_date,
        end_date,
        limit = 100,
        offset = 0
      } = filters;

      const params = new URLSearchParams();
      if (status) params.append('status', status);
      if (trip_id) params.append('trip_id', trip_id);
      if (start_date) params.append('start_date', start_date);
      if (end_date) params.append('end_date', end_date);
      params.append('limit', limit);
      params.append('offset', offset);

      const response = await fetch(
        `${ActivityService.API_BASE}?${params}`,
        { headers: AuthService.getJsonHeaders() }
      );

      const data = await AuthService.handleResponse(response);
      // Normalize activities to camelCase format
      return (data.activities || []).map(a => this.normalizeActivity(a));
    } catch (error) {
      console.error('Error getting activities:', error);
      return [];
    }
  }

  /**
   * Get scheduled activities (upcoming)
   * @returns {array} Array of scheduled activities
   */
  static async getScheduledActivities() {
    return this.getActivities({ status: 'planned' });
  }

  /**
   * Get completed activities (past) with separate needs_review and reviewed sections
   * @param {object} options - Pagination options
   * @param {number} options.reviewedLimit - Number of reviewed activities to fetch (default 20)
   * @param {number} options.reviewedOffset - Offset for reviewed activities pagination (default 0)
   * @returns {object} { needsReview: [], reviewed: [], reviewedTotal: number, hasMore: boolean }
   */
  static async getCompletedActivities(options = {}) {
    try {
      const { reviewedLimit = 20, reviewedOffset = 0, standaloneOnly = false } = options;
      const params = new URLSearchParams();
      params.append('reviewed_limit', reviewedLimit);
      params.append('reviewed_offset', reviewedOffset);
      if (standaloneOnly) params.append('standalone_only', 'true');

      const url = `${ActivityService.API_BASE}/completed?${params}`;
      const response = await fetch(url, { headers: AuthService.getJsonHeaders() });
      const data = await AuthService.handleResponse(response);

      return {
        needsReview: (data.needsReview || []).map(a => this.normalizeActivity(a)),
        needsReviewTotal: data.needsReviewTotal || 0,
        reviewed: (data.reviewed || []).map(a => this.normalizeActivity(a)),
        reviewedTotal: data.reviewedTotal || 0,
        hasMore: data.hasMore || false
      };
    } catch (error) {
      console.error('[ActivityService] Error getting completed activities:', error);
      return { needsReview: [], needsReviewTotal: 0, reviewed: [], reviewedTotal: 0, hasMore: false };
    }
  }

  /**
   * Get overdue activities (planned but date has passed)
   * These need user review to mark as completed or skipped
   * @returns {array} Array of overdue activities
   */
  static async getOverdueActivities() {
    try {
      // Get planned standalone activities whose date has passed (trip activities reviewed with the trip).
      // "Today" is the device's calendar date, not UTC's.
      const today = getLocalDateString(new Date());
      const params = new URLSearchParams();
      params.append('status', 'planned');
      params.append('end_date', today);
      params.append('trip_id', 'null');

      const response = await fetch(
        `${ActivityService.API_BASE}?${params}`,
        { headers: AuthService.getJsonHeaders() }
      );

      const data = await AuthService.handleResponse(response);
      return (data.activities || []).map(a => this.normalizeActivity(a));
    } catch (error) {
      console.error('Error getting overdue activities:', error);
      return [];
    }
  }

  /**
   * Update activity status (mark as skipped, cancelled, etc.)
   * @param {string} activityId - Activity ID
   * @param {string} status - New status ('planned', 'in_progress', 'completed', 'skipped', 'cancelled', 'archived')
   * @returns {object} Result with success boolean
   */
  static async updateActivityStatus(activityId, status) {
    try {
      const response = await fetch(
        `${ActivityService.API_BASE}/${activityId}/status`,
        {
          method: 'PATCH',
          headers: AuthService.getJsonHeaders(),
          body: JSON.stringify({ status })
        }
      );

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error updating activity status:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Rateable activities for one calendar day (standalone + during-trip) —
   * backs the "How was yesterday?" rating sheet (/rate-day/:date) that the
   * activity_rating_nudge notification deep-links to.
   * @param {string} date - 'YYYY-MM-DD'
   * @returns {array} [{ id, title, location, placeId, scheduledTime, status,
   *   tripId, tripDayId, tripName, photoUrl, scheduledDate, category,
   *   coordinates, lat, lng, types }] — the trailing fields exist so a row can
   *   feed buildReviewActivityShape and open the full review modal in place.
   */
  static async getRatingDayActivities(date) {
    try {
      const response = await fetch(
        `${API_BASE}/api/activities/rating-day/${date}`,
        { headers: AuthService.getJsonHeaders() }
      );
      const data = await AuthService.handleResponse(response);
      return data.activities || [];
    } catch (error) {
      console.error('Error getting rating day activities:', error);
      return [];
    }
  }

  /**
   * Get upcoming standalone activities (not part of any trip)
   *
   * Filtered server-side to planned activities from today onward — both
   * callers (planner Scheduled tab, notification reminders) only use future
   * planned items, and the unfiltered query returned the 100 most recent
   * rows of ANY status (~220KB, all discarded client-side). Past planned
   * items come from getOverdueActivities; completed from getCompletedActivities.
   * @returns {array} Array of standalone activities
   */
  static async getStandaloneActivities({ withCover = false } = {}) {
    try {
      // trip_id=null returns standalone activities
      const params = new URLSearchParams();
      params.append('trip_id', 'null');
      params.append('status', 'planned');
      // The device's calendar date, not UTC's: in the Americas UTC's date
      // rolls over in the afternoon and today's plans dropped off the list.
      params.append('start_date', getLocalDateString(new Date()));
      // The cascade cover, for callers that render a photo (the planner's
      // Scheduled tab). Reminders don't, so they skip the server's lookup.
      if (withCover) params.append('with_cover', '1');

      const response = await fetch(
        `${ActivityService.API_BASE}?${params}`,
        { headers: AuthService.getJsonHeaders() }
      );

      const data = await AuthService.handleResponse(response);
      // Normalize activities to camelCase format
      return (data.activities || []).map(a => this.normalizeActivity(a));
    } catch (error) {
      console.error('Error getting standalone activities:', error);
      return [];
    }
  }

  /**
   * Get activities for a specific trip
   * @param {string} tripId - Trip ID
   * @returns {array} Array of trip activities
   */
  static async getTripActivities(tripId) {
    try {
      const response = await fetch(
        `${ActivityService.API_BASE}/trip/${tripId}`,
        { headers: AuthService.getJsonHeaders() }
      );

      const data = await AuthService.handleResponse(response);
      return data.activities || [];
    } catch (error) {
      console.error('Error getting trip activities:', error);
      return [];
    }
  }

  /**
   * Get attendees (adult + child) and saved child reviews for an activity.
   * Used by VisitConfirmationModal in edit mode.
   * Note: Uses /api/activities/:id/attendees (not the activity-instances base).
   * @param {string|number} activityId
   * @returns {Promise<{ success, attendees, childReviews, error? }>}
   */
  static async getActivityAttendees(activityId) {
    try {
      const response = await fetch(
        `${API_BASE}/api/activities/${activityId}/attendees`,
        { headers: AuthService.getAuthHeaders() }
      );
      const data = await response.json();
      if (!response.ok || !data.success) {
        return { success: false, attendees: [], childReviews: [], error: data.error || 'Failed to load attendees' };
      }
      return {
        success: true,
        attendees: data.attendees || [],
        childReviews: data.childReviews || []
      };
    } catch (error) {
      console.error('Error getting activity attendees:', error);
      return { success: false, attendees: [], childReviews: [], error: error.message };
    }
  }

  /**
   * Get user's activity statistics
   * @returns {object} Activity stats
   */
  static async getActivityStats() {
    try {
      const response = await fetch(
        `${ActivityService.API_BASE}/stats`,
        { headers: AuthService.getJsonHeaders() }
      );

      const data = await AuthService.handleResponse(response);
      return data.stats || {};
    } catch (error) {
      console.error('Error getting activity stats:', error);
      return {};
    }
  }

  // =====================================================
  // UPDATE & COMPLETE ACTIVITIES
  // =====================================================

  /**
   * Update an activity instance
   * @param {string} activityId - Activity ID
   * @param {object} updates - Fields to update
   * @returns {object} Result
   */
  /**
   * Fetch existing reviews for a list of activity IDs.
   * @param {string[]} activityIds
   * @param {{ nullOnError?: boolean }} [options] - nullOnError: a failed read
   *   answers null, not [], so a caller can tell "no reviews" from "didn't load"
   *   (the tips editor must not save an id-aware set it never loaded, W5)
   * @returns {Promise<object[]|null>} reviews keyed by backend shape
   */
  static async getReviewsForActivities(activityIds, { nullOnError = false } = {}) {
    try {
      if (!Array.isArray(activityIds) || activityIds.length === 0) return [];
      const url = `${ActivityService.API_BASE}/reviews?activityIds=${activityIds.map(encodeURIComponent).join(',')}`;
      const response = await fetch(url, { headers: AuthService.getJsonHeaders() });
      const data = await AuthService.handleResponse(response);
      if (nullOnError && !Array.isArray(data?.reviews)) return null;
      return data.reviews || [];
    } catch (error) {
      console.error('Error fetching activity reviews:', error);
      return nullOnError ? null : [];
    }
  }

  /**
   * Fetch the user's own photos for an activity. Used to seed the review
   * modal's existingPhotos when opened from the recap rating prompt.
   * @param {string} activityId
   * @param {{ nullOnError?: boolean }} [options] - nullOnError: return null,
   *   not [], when the load fails, for callers that must tell "no photos"
   *   from "couldn't load" (the review modal's order save)
   * @returns {Promise<object[]|null>}
   */
  static async getPhotosForActivity(activityId, { nullOnError = false } = {}) {
    try {
      if (!activityId) return [];
      const url = `${ActivityService.API_BASE}/${encodeURIComponent(activityId)}/photos`;
      const response = await fetch(url, { headers: AuthService.getJsonHeaders() });
      const data = await AuthService.handleResponse(response);
      if (!Array.isArray(data?.photos)) return nullOnError ? null : [];
      return data.photos;
    } catch (error) {
      console.error('Error fetching activity photos:', error);
      return nullOnError ? null : [];
    }
  }

  /**
   * The review sheet's photo suggestions for a stop: the user's own uploaded
   * photos of its day, the likely ones marked (`suggested`), each saying
   * whether another stop holds it. Shape on the server route
   * (GET /api/activity-instances/:id/photo-suggestions).
   * @param {string} activityId
   * @returns {Promise<object|null>} null when there's nothing to offer or the
   *   load failed — the sheet then shows only its own photos, as before
   */
  static async getPhotoSuggestions(activityId) {
    try {
      if (!activityId) return null;
      let tz = '';
      try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (e) { /* no tz: the server resolves one */ }
      const url = `${ActivityService.API_BASE}/${encodeURIComponent(activityId)}/photo-suggestions${tz ? `?tz=${encodeURIComponent(tz)}` : ''}`;
      const response = await fetch(url, { headers: AuthService.getJsonHeaders() });
      const data = await AuthService.handleResponse(response);
      return Array.isArray(data?.photos) ? data : null;
    } catch (error) {
      console.error('Error fetching photo suggestions:', error);
      return null;
    }
  }

  /**
   * The review sheet's "Cost per person" prefill from the trip expenses linked
   * to a stop. Shape on the server route
   * (GET /api/activity-instances/:id/expense-cost).
   * @param {string} activityId
   * @returns {Promise<object|null>} { cost_per_person, currency, converted,
   *   expenses }, or null when there's nothing to fill or the load failed
   */
  static async getExpenseCostPrefill(activityId) {
    try {
      if (!activityId) return null;
      const url = `${ActivityService.API_BASE}/${encodeURIComponent(activityId)}/expense-cost`;
      const response = await fetch(url, { headers: AuthService.getJsonHeaders() });
      const data = await AuthService.handleResponse(response);
      return data?.prefill && Number(data.prefill.cost_per_person) > 0 ? data.prefill : null;
    } catch (error) {
      console.error('Error fetching expense cost:', error);
      return null;
    }
  }

  /**
   * Submit one or more activity reviews via the existing batch endpoint.
   * @param {object[]} reviews - [{ activityId, rating?, wouldReturn?, ... }]
   * @returns {Promise<{ success, updatedCount?, error? }>}
   */
  static async submitReviews(reviews) {
    try {
      const response = await fetch(`${ActivityService.API_BASE}/reviews/batch`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({ reviews })
      });
      const result = await AuthService.handleResponse(response);
      if (result?.success !== false) {
        // Reviewing completes the activity server-side and awards badges in
        // the same request, so poll for unlocks exactly as completeActivity
        // does — otherwise a badge earned by rating from the rating sheet or
        // the recap composer stays silent until the next page load.
        const { requestBadgeUnlockCheck } = await import('../utils/badgeUnlockEvents');
        requestBadgeUnlockCheck();
      }
      return result;
    } catch (error) {
      console.error('Error submitting reviews:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * The planner's "How did it go?" strip: the viewer's unreviewed days,
   * newest first, each with the moment it becomes reviewable, plus today's
   * moment (server/services/reviewQueueService.js). `today` is the device's
   * local date — the server only knows UTC.
   * @param {string} today - YYYY-MM-DD
   * @returns {Promise<{ days: object[], today: { date: string, reviewableFrom: object|null } }|null>}
   *   null on failure, so a caller can keep what it has
   */
  static async getReviewQueue(today) {
    try {
      const response = await fetch(
        `${ActivityService.API_BASE}/review-queue?today=${encodeURIComponent(today)}`,
        { headers: AuthService.getJsonHeaders() }
      );
      const result = await AuthService.handleResponse(response);
      if (!result || result.success === false) return null;
      return {
        days: result.days || [],
        today: { date: today, reviewableFrom: result.today_reviewable_from || null }
      };
    } catch (error) {
      console.error('Error loading review queue:', error);
      return null;
    }
  }

  /**
   * The planner's Memories: trips and days, newest first, each with who can
   * see it and whether it was posted (server/services/plannerMemoriesService.js).
   * @param {object} params - { today, q, kind, notShared, childId, year, offset, limit }
   * @returns {Promise<{ memories, total, all_total, years, next_offset }|null>} null on failure
   */
  static async getPlannerMemories({ today, q, kind, notShared, childId, year, offset = 0, limit = 30 } = {}) {
    try {
      const params = new URLSearchParams({ today, offset: String(offset), limit: String(limit) });
      if (q) params.set('q', q);
      if (kind) params.set('kind', kind);
      if (notShared) params.set('not_shared', '1');
      if (childId) params.set('child_id', childId);
      if (year) params.set('year', year);
      const response = await fetch(`${ActivityService.API_BASE}/memories?${params.toString()}`, {
        headers: AuthService.getJsonHeaders()
      });
      const result = await AuthService.handleResponse(response);
      return result && result.success !== false ? result : null;
    } catch (error) {
      console.error('Error loading memories:', error);
      return null;
    }
  }

  /**
   * "Skip" on a whole day of the review strip (dismissed=true), or its Undo:
   * stops asking about the day's stops without hiding their stars anywhere.
   * @param {string[]} activityIds
   * @param {boolean} dismissed
   * @returns {Promise<{ success, updated?, error? }>}
   */
  static async setReviewDismissalBatch(activityIds, dismissed) {
    try {
      const response = await fetch(`${ActivityService.API_BASE}/review-dismissal/batch`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({ activityIds, dismissed })
      });
      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error updating review dismissals:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * "Skip" on one stop's review, or its Undo: sets only review_dismissed_at
   * (the review strip's day Skip, one stop at a time), so the asking stops —
   * the strip, the nudge, /rate-day, the planner's own lists — and nothing
   * else. Never setReviewExclusion, which voids the stop's whole review.
   * The server leaves a stop alone that isn't the caller's or is rated
   * already; that answers as a failure here, so the caller rolls back.
   * @param {string} activityId
   * @param {boolean} dismissed
   * @returns {Promise<{ success: boolean, error?: string }>}
   */
  static async setReviewDismissed(activityId, dismissed) {
    try {
      const response = await fetch(`${ActivityService.API_BASE}/review-dismissal/batch`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({ activityIds: [activityId], dismissed })
      });
      const result = await AuthService.handleResponse(response);
      if (!result || result.success === false) return { success: false, error: result?.error };
      return { success: result.updated > 0 };
    } catch (error) {
      console.error('Error updating review dismissal:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Toggle whether an activity is excluded from review prompts ("skip review").
   * Setting excluded=true also voids any existing rating server-side, so the
   * activity drops out of every needs-review list and rating nudge.
   * @param {string} activityId
   * @param {boolean} excluded
   * @returns {Promise<{ success, excludeFromReview?, error? }>}
   */
  static async setReviewExclusion(activityId, excluded) {
    try {
      const response = await fetch(`${ActivityService.API_BASE}/${encodeURIComponent(activityId)}/review-exclusion`, {
        method: 'PATCH',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({ excluded })
      });
      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error updating review exclusion:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Replace the user's visitor tips on one activity (full replace-set).
   * Unlike submitReviews, this never marks the activity completed — safe to
   * call from the planner before a visit.
   * @param {string} activityId
   * @param {object[]} tips - [{ id?, category, text }]; loaded tips keep their id
   * @param {{ idAware?: boolean }} [options] - idAware: the list came from the
   *   author's own read (moderated tips included), so a tip left out of it was
   *   deleted (src/utils/tipPayload.js)
   * @returns {Promise<{ success, tips?, dropped?, error?, notFound?, status? }>}
   *   status (on a failure): the HTTP status, or null when no response came
   *   back (network error) — so a caller can tell a refusal (4xx) from a
   *   failure worth sending again (CC-07-02)
   */
  static async updateActivityTips(activityId, tips, { idAware = false } = {}) {
    let status = null;
    try {
      const body = { tips: tipsForSave(tips) };
      if (idAware) body.idAware = true;
      const response = await fetch(
        `${ActivityService.API_BASE}/${encodeURIComponent(activityId)}/tips`,
        {
          method: 'PUT',
          headers: AuthService.getJsonHeaders(),
          body: JSON.stringify(body)
        }
      );
      status = response.status;
      // 404: the caller's own activity is gone for them (in their trash, or
      // moderated past them), so the edit can never land. Flagged apart from
      // other refusals — a co-traveller's save is refused with a 400, and
      // callers keep that one quiet.
      if (response.status === 404) {
        const body = await response.json().catch(() => ({}));
        return { success: false, notFound: true, status, error: body.error || 'Activity not found' };
      }
      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error saving activity tips:', error);
      return { success: false, error: error.message, status };
    }
  }

  static async updateActivity(activityId, updates) {
    try {
      const response = await fetch(
        `${ActivityService.API_BASE}/${activityId}`,
        {
          method: 'PUT',
          headers: AuthService.getJsonHeaders(),
          body: JSON.stringify(updates)
        }
      );

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error updating activity:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Complete an activity with rating and feedback.
   * Photos are uploaded separately via the multipart /visits/photo path.
   * @param {string} activityId - Activity ID
   * @param {number} rating - Rating 1-5 (required)
   * @param {string} feedback - Optional feedback text
   * @returns {object} Result
   */
  static async completeActivity(activityId, rating, feedback = '') {
    try {
      const response = await fetch(
        `${ActivityService.API_BASE}/${activityId}/complete`,
        {
          method: 'POST',
          headers: AuthService.getJsonHeaders(),
          body: JSON.stringify({
            rating,
            feedback
          })
        }
      );

      const result = await AuthService.handleResponse(response);
      if (result?.success !== false) {
        // Server awards badges right after the completion response — poll
        // for unlocks so the celebration fires at the moment it was earned
        const { requestBadgeUnlockCheck } = await import('../utils/badgeUnlockEvents');
        requestBadgeUnlockCheck();
      }
      return result;
    } catch (error) {
      console.error('Error completing activity:', error);
      return { success: false, error: error.message };
    }
  }

  // =====================================================
  // DELETE ACTIVITIES
  // =====================================================

  /**
   * Delete an activity (soft delete)
   * @param {string} activityId - Activity ID
   * @returns {object} Result
   */
  static async deleteActivity(activityId) {
    try {
      const response = await fetch(
        `${ActivityService.API_BASE}/${activityId}`,
        {
          method: 'DELETE',
          headers: AuthService.getJsonHeaders()
        }
      );

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error deleting activity:', error);
      return { success: false, error: error.message };
    }
  }

  // =====================================================
  // LEGACY COMPATIBILITY
  // =====================================================

  /**
   * Get activities for a specific date (legacy compatibility)
   * @param {string} date - Date in YYYY-MM-DD format
   * @returns {array} Activities for that date
   */
  static async getActivitiesForDate(date) {
    return this.getActivities({
      start_date: date,
      end_date: date,
      status: 'planned'
    });
  }

  /**
   * Get past activities (legacy compatibility)
   * Alias for getCompletedActivities()
   */
  static async getPastActivities() {
    return this.getCompletedActivities();
  }

  /**
   * Get saved activities (legacy compatibility)
   * This now returns all scheduled activities
   */
  static async getSavedActivities() {
    return this.getScheduledActivities();
  }
}

export default ActivityService;
